import React from 'react';
import {
  Autocomplete,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ATTENDANCE_PERIODS,
  monthOptions,
} from '../../utils/attendanceFilters';
import { weekEndForStart } from '../../utils/reportFilters';
import { getCountries } from '../../data/locationData';

const ALL_BATCHES = { id: '', name: 'All batches' };
const ALL_STUDENTS = { studentId: '', studentName: 'All students' };
const ALL_TEACHERS = { id: '', label: 'All teachers' };
const ALL_COUNTRIES = { value: '', label: 'All countries' };

function matchesQuery(parts, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q);
}

function SearchSelect({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  isOptionEqualToValue,
  filterParts,
  minWidth,
}) {
  return (
    <Autocomplete
      size="small"
      options={options}
      value={value ?? null}
      onChange={(_e, next) => onChange(next)}
      getOptionLabel={(option) => getOptionLabel(option) || ''}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={(opts, state) =>
        opts.filter((option) => matchesQuery(filterParts(option), state.inputValue))
      }
      autoHighlight
      autoComplete
      includeInputInList
      clearOnEscape
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Type to search" />
      )}
      sx={{ minWidth }}
    />
  );
}

export default function AttendanceFilterBar({
  filters,
  onChange,
  batches = [],
  students = [],
  teachers = [],
  showTeacherFilter = false,
  showTeamToggle = false,
  allowAllTeachers = false,
  compact = false,
}) {
  const set = (patch) => onChange({ ...filters, ...patch });

  const batchOptions = [ALL_BATCHES, ...batches];
  const selectedBatch =
    batchOptions.find((b) => String(b.id) === String(filters.batchId || '')) ||
    (filters.batchId ? { id: filters.batchId, name: 'Selected batch' } : ALL_BATCHES);

  const studentOptions = [ALL_STUDENTS, ...students];
  const selectedStudent =
    studentOptions.find((s) => String(s.studentId) === String(filters.studentId || '')) ||
    (filters.studentId
      ? { studentId: filters.studentId, studentName: 'Selected student' }
      : ALL_STUDENTS);

  const teacherOptions = allowAllTeachers ? [ALL_TEACHERS, ...teachers] : teachers;
  const selectedTeacher =
    teacherOptions.find((t) => String(t.id) === String(filters.teacherId || '')) ||
    (filters.teacherId ? { id: filters.teacherId, label: 'Selected teacher' } : null);

  const countryOptions = [ALL_COUNTRIES, ...getCountries().map((country) => ({ value: country, label: country }))];
  const selectedCountry =
    countryOptions.find((c) => c.value === String(filters.country || '')) ||
    (filters.country ? { value: filters.country, label: filters.country } : ALL_COUNTRIES);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={filters.period}
        onChange={(_, v) => v && set({ period: v })}
      >
        {ATTENDANCE_PERIODS.map((p) => (
          <ToggleButton key={p.value} value={p.value}>
            {p.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {filters.period === 'day' ? (
        <TextField
          size="small"
          type="date"
          label="Date"
          value={filters.date}
          onChange={(e) => set({ date: e.target.value })}
          InputLabelProps={{ shrink: true }}
        />
      ) : null}

      {filters.period === 'week' ? (
        <TextField
          size="small"
          type="date"
          label="Week starting (Mon)"
          value={filters.weekStart}
          onChange={(e) => set({ weekStart: e.target.value })}
          InputLabelProps={{ shrink: true }}
          helperText={filters.weekStart ? `Through ${weekEndForStart(filters.weekStart)}` : ''}
        />
      ) : null}

      {filters.period === 'month' ? (
        <TextField
          select
          size="small"
          label="Month"
          value={filters.month}
          onChange={(e) => set({ month: e.target.value })}
          sx={{ minWidth: 200 }}
        >
          {monthOptions().map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {filters.period === 'custom' ? (
        <>
          <TextField
            size="small"
            type="date"
            label="From"
            value={filters.from}
            onChange={(e) => set({ from: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={filters.to}
            onChange={(e) => set({ to: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </>
      ) : null}

      <SearchSelect
        label="Batch"
        options={batchOptions}
        value={selectedBatch}
        onChange={(next) => set({ batchId: next?.id || '' })}
        getOptionLabel={(option) => option?.name || ''}
        isOptionEqualToValue={(a, b) => String(a?.id || '') === String(b?.id || '')}
        filterParts={(option) => [option?.name]}
        minWidth={compact ? 180 : 240}
      />

      <SearchSelect
        label="Student"
        options={studentOptions}
        value={selectedStudent}
        onChange={(next) => set({ studentId: next?.studentId || '' })}
        getOptionLabel={(option) => {
          if (!option?.studentId) return option?.studentName || 'All students';
          return option.displayId
            ? `${option.studentName} (${option.displayId})`
            : option.studentName || '';
        }}
        isOptionEqualToValue={(a, b) => String(a?.studentId || '') === String(b?.studentId || '')}
        filterParts={(option) => [option?.studentName, option?.displayId, option?.batchName]}
        minWidth={compact ? 200 : 280}
      />

      {showTeacherFilter ? (
        <SearchSelect
          label="Teacher"
          options={teacherOptions}
          value={selectedTeacher}
          onChange={(next) => set({ teacherId: next?.id || '', teamView: false })}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(a, b) => String(a?.id || '') === String(b?.id || '')}
          filterParts={(option) => [option?.label]}
          minWidth={compact ? 180 : 240}
        />
      ) : null}

      <SearchSelect
        label="Country"
        options={countryOptions}
        value={selectedCountry}
        onChange={(next) => set({ country: next?.value || '' })}
        getOptionLabel={(option) => option?.label || ''}
        isOptionEqualToValue={(a, b) => String(a?.value || '') === String(b?.value || '')}
        filterParts={(option) => [option?.label]}
        minWidth={compact ? 160 : 200}
      />

      {showTeamToggle ? (
        <FormControlLabel
          control={
            <Switch
              checked={!!filters.teamView}
              onChange={(e) =>
                set({
                  teamView: e.target.checked,
                  teacherId: e.target.checked ? '' : filters.teacherId,
                })
              }
            />
          }
          label="All my team"
        />
      ) : null}
    </Stack>
  );
}
